import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, inject } from '@angular/core';

/**
 * Pins the frozen (`.st`) columns of a `table.zf-sheet` at their real
 * positions. The templates give each frozen cell a hard-coded `--l` offset
 * (e.g. 46px / 130px), but the browser sizes columns from their content, so
 * the real widths drift from those numbers: the last frozen column then
 * covers the start of the first scrolling column, and scrolling shows other
 * columns through the gaps between frozen ones.
 *
 * This measures the header cells' actual offsets and writes them as inline
 * `left` on every frozen cell (header, body and footer, honouring colspan),
 * again whenever the table resizes or its rows change.
 *
 * Usage: `<table class="zf-sheet" appStickyCols>`.
 */
@Directive({ selector: 'table[appStickyCols]', standalone: true })
export class StickyColsDirective implements AfterViewInit, OnDestroy {
  private host = inject<ElementRef<HTMLTableElement>>(ElementRef);
  private zone = inject(NgZone);
  private resize?: ResizeObserver;
  private mutation?: MutationObserver;
  private timer: ReturnType<typeof setTimeout> | undefined;

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      const table = this.host.nativeElement;
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(table);
      const head = table.tHead?.rows[0];
      if (head) Array.from(head.cells).forEach((c) => this.resize!.observe(c));
      this.mutation = new MutationObserver(() => this.schedule());
      this.mutation.observe(table, { childList: true, subtree: true });
      this.schedule();
    });
  }

  ngOnDestroy(): void {
    this.resize?.disconnect();
    this.mutation?.disconnect();
    clearTimeout(this.timer);
  }

  // A timer, not requestAnimationFrame: rAF is paused while the tab/window
  // is hidden, which would leave the columns unpinned until it is shown.
  private schedule(): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.apply(), 0);
  }

  private apply(): void {
    const table = this.host.nativeElement;
    const head = table.tHead?.rows[0];
    if (!head) return;
    // Natural left of every column = sum of the widths before it (header row,
    // no colspans there). Not offsetLeft: for a sticky cell that already
    // includes the sticky shift, i.e. the very offset being corrected.
    const lefts: number[] = [];
    let x = 0;
    for (const c of Array.from(head.cells)) {
      lefts.push(x);
      x += c.offsetWidth;
    }
    if (!lefts.length) return;

    for (const row of Array.from(table.rows)) {
      let col = 0;
      for (const cell of Array.from(row.cells)) {
        if (cell.classList.contains('st')) {
          const left = lefts[col] ?? lefts[lefts.length - 1];
          const px = `${Math.round(left)}px`;
          if (cell.style.left !== px) cell.style.left = px;
        }
        col += cell.colSpan || 1;
      }
    }
  }
}
